import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { isGone, spawnBurst, stepParticle, type Particle } from './confettiPhysics';
import styles from './BlogScreen.module.css';

/** Los tokens de donde salen los colores: los del confeti y los de las tarjetas. */
const COLOR_TOKENS = [
  '--blog-confetti-1',
  '--blog-confetti-2',
  '--blog-confetti-3',
  '--blog-confetti-4',
  '--blog-tile-2',
  '--blog-tile-5',
  '--blog-tile-1',
] as const;

const PARTICLES = 180;
/** Un paso nunca más largo que esto: al volver a una pestaña dormida no salta todo de golpe. */
const MAX_STEP_S = 1 / 30;

const prefersReducedMotion = (): boolean =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const readColors = (): string[] => {
  const css = getComputedStyle(document.documentElement);
  return COLOR_TOKENS.map((token) => css.getPropertyValue(token).trim()).filter((c) => c.length > 0);
};

/**
 * Una ráfaga de confeti sobre toda la ventana —modales incluidos— cada vez que
 * cambia `burst`.
 *
 * Un `<canvas>` que no atrapa el ratón —se puede seguir usando la pantalla
 * mientras cae— y que se borra solo al terminar. **Quien pidió a su sistema
 * menos movimiento no lo ve**: para alguien con vértigo, 180 papeles cruzando la
 * pantalla no son una fiesta.
 */
export const Confetti = ({ burst }: { readonly burst: number }): ReactNode => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null || prefersReducedMotion()) return;
    const context = canvas.getContext('2d');
    if (context === null) return;

    const ratio = window.devicePixelRatio || 1;
    const width = window.innerWidth;
    const height = window.innerHeight;
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
    context.setTransform(ratio, 0, 0, ratio, 0, 0);

    let particles: Particle[] = spawnBurst({ count: PARTICLES, width, height, colors: readColors() });
    let last = performance.now();
    let frame = 0;

    const draw = (): void => {
      context.clearRect(0, 0, width, height);
      for (const p of particles) {
        context.save();
        context.translate(p.x, p.y);
        context.rotate(p.rotation);
        /* El vaivén también encoge el papel de lado: es lo que lo hace girar en el aire. */
        context.scale(1, Math.cos(p.wobble));
        context.fillStyle = p.color;
        context.fillRect(-p.width / 2, -p.height / 2, p.width, p.height);
        context.restore();
      }
    };

    const tick = (now: number): void => {
      const dt = Math.min(MAX_STEP_S, (now - last) / 1000);
      last = now;
      particles = particles.map((p) => stepParticle(p, dt)).filter((p) => !isGone(p, height));
      draw();
      if (particles.length > 0) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frame);
      context.clearRect(0, 0, width, height);
    };
  }, [burst]);

  /*
   * Directo en `body`, como los modales: así cae también **encima** de la
   * felicitación al cumpleañero. Dentro del árbol del portal heredaría la capa de
   * quien lo pinta y se quedaría debajo de la ventana.
   */
  return createPortal(
    <canvas ref={canvasRef} className={styles.confetti} aria-hidden="true" />,
    document.body,
  );
};
